/**
 * ConfigureHelpCallout (#233) -- inline "What you'll need" guidance shown at the
 * top of the Configure step. Respects #260's full-width Configure layout (this
 * is an inline callout, NOT a returned side panel). Content is derived from the
 * connector schema via deriveConnectorHelp().
 */
import React from 'react';
import { Info, ExternalLink, KeyRound } from 'lucide-react';
import { Alert } from '@/components/ui/alert';
import type { ConnectorTypeSchemaResponse } from '@/types/connector-schema';
import { deriveConnectorHelp, type ConnectorHelpLinks } from './connectorHelpContent';

export interface ConfigureHelpCalloutProps {
  connectorType: string;
  schema: ConnectorTypeSchemaResponse | undefined;
  /** Docs links from the connector-types list (the /schema endpoint omits them). */
  links?: ConnectorHelpLinks;
}

const DocLink: React.FC<{ href: string; label: string }> = ({ href, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline underline-offset-4"
  >
    <ExternalLink className="h-3.5 w-3.5" />
    {label}
  </a>
);

export const ConfigureHelpCallout: React.FC<ConfigureHelpCalloutProps> = ({
  connectorType,
  schema,
  links,
}) => {
  const help = deriveConnectorHelp(schema, connectorType, links);

  return (
    <Alert variant="info" aria-label="What you'll need">
      <Info className="h-4 w-4" />
      <div className="text-foreground">
        <p className="mb-2 font-medium leading-none">What you'll need</p>

        <ul className="ml-4 list-disc space-y-1 text-sm">
          {help.needs.map((need) => (
            <li key={need}>{need}</li>
          ))}
        </ul>

        {help.oauthNote && (
          <p className="mt-2 flex items-start gap-1.5 text-sm text-muted-foreground">
            <KeyRound className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span>{help.oauthNote}</span>
          </p>
        )}

        {help.note && (
          <p className="mt-2 text-sm text-muted-foreground">{help.note}</p>
        )}

        {(help.docsUrl || help.setupUrl) && (
          <div className="mt-2.5 flex flex-wrap gap-4">
            {help.docsUrl && (
              <DocLink href={help.docsUrl} label="API documentation" />
            )}
            {help.setupUrl && (
              <DocLink href={help.setupUrl} label="Setup guide" />
            )}
          </div>
        )}
      </div>
    </Alert>
  );
};
